/**
 * OnboardingTrigger Component
 * 
 * A button/component that starts the onboarding flow.
 * Can be placed in settings, profile dropdown, or shown to new users.
 */

import { useOnboardingContext } from '../../context/OnboardingContext'
import { Icon } from '../common/Icon'
import { Button } from '../common/Button'
import './OnboardingTrigger.css'

/**
 * OnboardingStartButton - Button to start onboarding
 */
export function OnboardingStartButton({ variant = 'default' }) {
  const { needsOnboarding, startOnboarding, restartOnboarding, isCompleted } = useOnboardingContext()

  const handleClick = async () => {
    if (isCompleted) {
      await restartOnboarding()
    } else {
      await startOnboarding()
    }
  }

  if (variant === 'minimal') {
    return (
      <button 
        className="onboarding-trigger onboarding-trigger--minimal"
        onClick={handleClick}
        title={isCompleted ? 'Restart tour' : 'Start tour'}
      >
        <Icon name="info" size={16} />
        <span>{isCompleted ? 'Restart tour' : 'Take a tour'}</span>
      </button>
    )
  }

  return (
    <Button
      variant="outline"
      size="sm"
      leftIcon={<Icon name="info" size={14} />}
      onClick={handleClick}
      className="onboarding-trigger"
    >
      {isCompleted ? 'Restart tour' : 'Take a tour'}
    </Button>
  )
}

/**
 * OnboardingPrompt - Prompt card shown to new users
 */
export function OnboardingPrompt({ onDismiss }) {
  const { needsOnboarding, startOnboarding } = useOnboardingContext()

  if (!needsOnboarding) return null

  const handleStart = async () => {
    await startOnboarding()
  }

  const handleDismiss = () => {
    onDismiss?.()
  }

  return (
    <div className="onboarding-prompt">
      <div className="onboarding-prompt__icon">
        <Icon name="info" size={24} />
      </div>
      <div className="onboarding-prompt__content">
        <h4 className="onboarding-prompt__title">New to Plot Planet?</h4>
        <p className="onboarding-prompt__description">
          Take a quick tour to learn how to build your story universe.
        </p>
      </div>
      <div className="onboarding-prompt__actions">
        <Button variant="primary" size="sm" onClick={handleStart}>
          Start tour
        </Button>
        <button className="onboarding-prompt__dismiss" onClick={handleDismiss}>
          Maybe later
        </button>
      </div>
    </div>
  )
}

export default OnboardingStartButton
